import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../../services/hrmsService";
import { useMasterOptions } from "../hrForm";
import { buildEntriesPayload, cellInfo, coverageByDay, daysFrom, isoWeekday, mondayOf, WEEKDAYS } from "../timeHelpers";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton } from "../hrComponents";
import { addDaysYmd, cardStyle, formatDate, inputStyle, thStyle, tdStyle, todayYmd, useLoad } from "../hrUi";

// ROS-03/05/06: the roster board. Everyone the caller may see, two weeks at a time. Time admins click a day to set a
// shift (or a day off) for a range, see the rules it would break before saving, generate from patterns, and publish.
const DAYS = 14;
const DAY_NAME = Object.fromEntries(WEEKDAYS);

function CellDrawer({ target, shifts, onHide, onDone }) {
  // null = untouched: the shift and end date default from the clicked day, so there is no moment (before an effect
  // has run) when the form holds something else.
  const [chosenShift, setChosenShift] = useState(null);
  const [chosenTo, setChosenTo] = useState(null);
  const [reason, setReason] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    setChosenShift(null);
    setChosenTo(null);
    setReason("");
    setWarnings([]);
  }, [target]);
  const shiftId = chosenShift !== null ? chosenShift : target && target.cell && target.cell.shift_id ? target.cell.shift_id : "OFF";
  const to = chosenTo !== null ? chosenTo : target ? target.date : "";
  const setShiftId = setChosenShift;
  const setTo = setChosenTo;
  const payload = target ? buildEntriesPayload({ employeeId: target.person.employee_id, from: target.date, to: to || target.date, shiftId: shiftId === "OFF" ? null : shiftId, overrideReason: warnings.length ? reason : undefined }) : null;

  const check = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.previewRosterEntries(payload);
      if (!res?.success) return toast.error(hrmsService.responseMessage(res));
      setWarnings(res.data.warnings || []);
      return res.data.warnings || [];
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.saveRosterEntries(payload);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  // Check first; a clean check saves straight away, otherwise the warnings and the reason box appear.
  const submit = async () => {
    if (!warnings.length) {
      const found = await check();
      if (found === undefined || found.length) return;
    }
    await save();
  };

  return (
    <SlidePanel
      show={!!target}
      onHide={() => !saving && onHide()}
      title={target ? `${target.person.full_name} · ${formatDate(target.date)}` : ""}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || !payload || (warnings.length > 0 && !reason.trim())}>
            {warnings.length ? "Save anyway" : "Save"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Shift">
        <AppDropdown value={shiftId} options={[{ label: "Day off", value: "OFF" }, ...shifts.map((s) => ({ label: `${s.code} · ${s.name} (${String(s.start_time).slice(0, 5)}-${String(s.end_time).slice(0, 5)})`, value: s.id }))]} onChange={(e) => { setShiftId(e.value); setWarnings([]); }} />
      </Field>
      <Field label="Apply until" hint="Every day from the one you clicked up to this date.">
        <input type="date" className="form-control" style={inputStyle} value={to || ""} min={target?.date} onChange={(e) => { setTo(e.target.value); setWarnings([]); }} />
      </Field>
      {warnings.length ? (
        <div style={{ background: "#FEF3C7", borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 12 }} role="alert">
          <strong>This breaks roster rules:</strong>
          {warnings.map((w) => (
            <div key={`${w.employee_id}${w.date}${w.code}`}>
              {formatDate(w.date)}: {w.message}
            </div>
          ))}
          <Field label="Reason to save anyway" required>
            <textarea className="form-control mt-2" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      ) : null}
    </SlidePanel>
  );
}

function RangeDrawer({ title, show, onHide, onSubmit, initial, submitLabel }) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (show) {
      setFrom(initial.from);
      setTo(initial.to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const go = async () => {
    setSaving(true);
    try {
      const res = await onSubmit({ from, to });
      if (res?.success) {
        toast.success(res.message || "Done");
        onHide(true);
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide(false)}
      title={title}
      footer={
        <>
          <SecondaryButton onClick={() => onHide(false)} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={go} disabled={saving || !from || !to || to < from}>
            {submitLabel}
          </PrimaryButton>
        </>
      }
    >
      <div className="row">
        <div className="col-6">
          <Field label="From">
            <input type="date" className="form-control" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
        </div>
        <div className="col-6">
          <Field label="To">
            <input type="date" className="form-control" style={inputStyle} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </div>
    </SlidePanel>
  );
}

export default function RosterBoard({ canEdit }) {
  const [start, setStart] = useState(mondayOf(todayYmd()));
  const [department, setDepartment] = useState("ALL");
  const [cell, setCell] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const options = useMasterOptions(["departments"]);
  const end = addDaysYmd(start, DAYS - 1);
  const days = useMemo(() => daysFrom(start, DAYS), [start]);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getRoster({ from: start, to: end, department_id: department === "ALL" ? undefined : department }), [start, department]);
  const shifts = useMemo(() => data?.shifts || [], [data]);
  const shiftsById = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts]);
  const cover = data ? coverageByDay(data.people, days) : {};
  const c = { width: 46, minWidth: 46, height: 34, textAlign: "center", fontSize: 11, padding: 0, borderBottom: "1px solid #F3F4F6", borderRight: "1px solid #F9FAFB" };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Previous two weeks" onClick={() => setStart(addDaysYmd(start, -7))}>
          ‹
        </button>
        <strong style={{ minWidth: 190, textAlign: "center" }}>
          {formatDate(start)} – {formatDate(end)}
        </strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Next two weeks" onClick={() => setStart(addDaysYmd(start, 7))}>
          ›
        </button>
        <button type="button" className="btn btn-link btn-sm" onClick={() => setStart(mondayOf(todayYmd()))}>
          This week
        </button>
        <div style={{ width: 190 }}>
          <AppDropdown value={department} options={[{ label: "All departments", value: "ALL" }, ...(options.departments || [])]} onChange={(e) => setDepartment(e.value)} />
        </div>
        {canEdit ? (
          <div className="ms-auto d-flex gap-2">
            <SecondaryButton onClick={() => setGenerating(true)}>Generate from patterns</SecondaryButton>
            <PrimaryButton onClick={() => setPublishing(true)}>Publish</PrimaryButton>
          </div>
        ) : null}
      </div>

      <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: 12, color: "#6B7280" }}>
        {shifts.map((s) => (
          <span key={s.id}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: s.color || "#3b82f6", borderRadius: 2, marginRight: 4 }} />
            {s.code} {String(s.start_time).slice(0, 5)}-{String(s.end_time).slice(0, 5)}
          </span>
        ))}
        <span>Dashed = draft, not published yet</span>
      </div>

      <LoadState loading={loading} error={error} onRetry={reload} rows={6}>
        {!data || data.people.length === 0 ? (
          <EmptyState title="Nobody to show" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, position: "sticky", left: 0, zIndex: 1, minWidth: 150 }}>Employee</th>
                  {days.map((d) => (
                    <th key={d} style={{ ...c, background: "#F9FAFB", fontWeight: 600, color: isoWeekday(d) >= 6 ? "#B45309" : "#374151", height: 40 }}>
                      <div>{DAY_NAME[isoWeekday(d)]}</div>
                      <div style={{ fontWeight: 400 }}>{Number(d.slice(8))}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.employee_id}>
                    <td style={{ ...tdStyle, position: "sticky", left: 0, background: "#fff", whiteSpace: "nowrap", padding: "6px 12px" }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{p.full_name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280" }}>{p.department_name}</div>
                    </td>
                    {days.map((d) => {
                      const info = cellInfo(p.cells[d], shiftsById);
                      const style = { ...c, background: info.color, color: info.text, cursor: canEdit ? "pointer" : "default", border: info.draft ? "1px dashed #6B7280" : undefined, opacity: info.pending ? 0.6 : 1, fontWeight: 600 };
                      return (
                        <td key={d} title={info.title} style={style} onClick={canEdit ? () => setCell({ person: p, date: d, cell: p.cells[d] }) : undefined}>
                          {info.label}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td style={{ ...tdStyle, position: "sticky", left: 0, background: "#F9FAFB", fontSize: 12, fontWeight: 600 }}>Working / off / leave</td>
                  {days.map((d) => (
                    <td key={d} style={{ ...c, background: "#F9FAFB", fontSize: 10 }}>
                      {cover[d].working}/{cover[d].off}/{cover[d].leave}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </LoadState>

      <CellDrawer target={cell} shifts={shifts} onHide={() => setCell(null)} onDone={reload} />
      <RangeDrawer title="Generate the roster from patterns" show={generating} initial={{ from: start, to: end }} submitLabel="Generate" onHide={(done) => { setGenerating(false); if (done) reload(); }} onSubmit={(r) => hrmsService.generateRoster(r)} />
      <RangeDrawer title="Publish the roster" show={publishing} initial={{ from: start, to: end }} submitLabel="Publish and notify" onHide={(done) => { setPublishing(false); if (done) reload(); }} onSubmit={(r) => hrmsService.publishRoster(r)} />
    </>
  );
}
